import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { adminColors } from "../../theme/adminTheme";

const faviconUrl = (domain) => `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;

// Small clickable source pills rendered under a completed assistant answer,
// e.g. Claude/Perplexity-style "Sources: [domain] [domain] +N". Backed by
// the real per-URL citations extracted from the search (persisted via
// Message.search_citations so they survive a page refresh through /history).
const SearchSourcePills = ({ sources = [] }) => {
    const [expanded, setExpanded] = useState(false);

    if (!sources || sources.length === 0) return null;

    const MAX_VISIBLE = 3;
    const visible = expanded ? sources : sources.slice(0, MAX_VISIBLE);
    const remaining = sources.length - visible.length;

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 1.5 }}>
            {visible.map((source, i) => (
                <Box
                    key={source.url || i}
                    component="a"
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.6,
                        px: 1.1,
                        py: 0.45,
                        borderRadius: "999px",
                        border: "1px solid #e5e7eb",
                        backgroundColor: adminColors.surface,
                        textDecoration: "none",
                        color: adminColors.inkSoft,
                        maxWidth: 200,
                        "&:hover": { backgroundColor: adminColors.accentLight, color: adminColors.accent, borderColor: adminColors.accentBorder }
                    }}
                >
                    <Box
                        component="img"
                        src={faviconUrl(source.domain)}
                        alt=""
                        sx={{ width: 13, height: 13, borderRadius: "3px", flexShrink: 0 }}
                        onError={(e) => { e.target.style.visibility = "hidden"; }}
                    />
                    <Typography
                        variant="caption"
                        sx={{ fontSize: "11.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {source.domain || source.title}
                    </Typography>
                </Box>
            ))}
            {remaining > 0 && (
                <Box
                    component="button"
                    type="button"
                    onClick={() => setExpanded(true)}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.1,
                        py: 0.45,
                        borderRadius: "999px",
                        border: "1px solid transparent",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        font: "inherit",
                        "&:hover": { backgroundColor: adminColors.accentLight, borderColor: adminColors.accentBorder },
                        "&:hover .pill-more-label": { color: adminColors.accent }
                    }}
                >
                    <Typography className="pill-more-label" variant="caption" sx={{ fontSize: "11.5px", color: adminColors.inkSoft }}>
                        +{remaining} more
                    </Typography>
                </Box>
            )}
            {expanded && sources.length > MAX_VISIBLE && (
                <Box
                    component="button"
                    type="button"
                    onClick={() => setExpanded(false)}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.1,
                        py: 0.45,
                        borderRadius: "999px",
                        border: "1px solid transparent",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        font: "inherit",
                        "&:hover": { backgroundColor: adminColors.accentLight, borderColor: adminColors.accentBorder },
                        "&:hover .pill-less-label": { color: adminColors.accent }
                    }}
                >
                    <Typography className="pill-less-label" variant="caption" sx={{ fontSize: "11.5px", color: adminColors.inkSoft }}>
                        Show less
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default SearchSourcePills;
